import React, { useEffect, useState } from "react";
import { useManageProjectContext } from "../../../../context/ManageProjectContext";
import {
  AllActivity,
  updateProjectActivityPayload,
} from "../../../../model/projectModel";

interface ProjectActivityProps {
  isProjectClosed?: boolean;
}

const ProjectActivities: React.FC<ProjectActivityProps> = ({
  isProjectClosed = false,
}) => {
  const { projectDetail, updateProjectActivity, addProjectActivity, deleteProjectActivity } =
    useManageProjectContext();
  const [activityList, setActivityList] = useState<AllActivity[]>([]);
  const [searchActivitiesName, setActivitiesSearchName] = useState<string>("");

  // Add Activity modal state
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newActivityName, setNewActivityName] = useState<string>("");
  const [isAdding, setIsAdding] = useState<boolean>(false);

  // Delete confirmation modal & mandatory warning state
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [activityToDelete, setActivityToDelete] = useState<AllActivity | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [mandatoryWarning, setMandatoryWarning] = useState<string | null>(null);

  useEffect(() => {
    if (projectDetail) {
      setActivityList(projectDetail.activities ?? []);
    }
  }, [projectDetail]);

  const handleSelect = (id: number, checkbox: boolean) => {
    if (isProjectClosed) return;

    if (projectDetail) {
      const updatedActivities = activityList.map((activity) =>
        activity.version_id === id
          ? { ...activity, is_enabled: checkbox }
          : activity,
      );
      setActivityList(updatedActivities);
    }
  };

  const handleAddActivity = async () => {
    if (!newActivityName.trim() || !projectDetail) return;
    setIsAdding(true);
    try {
      await addProjectActivity([
        {
          projects_id: projectDetail.id,
          version_id: 0,
          is_enabled: true,
          activity_name: newActivityName.trim(),
        },
      ]);
      setNewActivityName("");
      setShowAddModal(false);
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteClick = (activity: AllActivity) => {
    if (activity.is_mandatory) {
      setMandatoryWarning("You cannot delete this activity as this activity is mandatory");
      return;
    }
    setActivityToDelete(activity);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!activityToDelete || !projectDetail) return;
    setIsDeleting(true);
    try {
      await deleteProjectActivity(projectDetail.id, activityToDelete.version_id);
      setActivityList((prev) =>
        prev.filter((a) => a.version_id !== activityToDelete.version_id)
      );
      setShowDeleteModal(false);
      setActivityToDelete(null);
    } catch (error) {
      console.error("Failed to delete activity:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const submitActivity = async () => {
    if (isProjectClosed) return;

    if (projectDetail && activityList) {
      const differingActivities = activityList.filter((activity) => {
        const correspondingActivity = projectDetail.activities.find(
          (item) => item.version_id === activity.version_id,
        );
        return (
          correspondingActivity &&
          correspondingActivity.is_enabled !== activity.is_enabled
        );
      });

      const activityDataPayload: updateProjectActivityPayload[] =
        differingActivities.map((item) => ({
          is_enabled: item.is_enabled,
          projects_id: projectDetail.id,
          version_id: item.version_id,
          activity_name: "",
        }));

      if (activityDataPayload && activityDataPayload.length > 0) {
        await updateProjectActivity(activityDataPayload);
      }
    }
  };

  return (
    <div className="max-w-2xl relative overflow-x-auto sm:rounded-lg">
      <div className="relative">
        <input
          type="search"
          id="default-search"
          value={searchActivitiesName}
          onChange={(e) => setActivitiesSearchName(e.target.value)}
          className="block w-full p-3 mb-3 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          placeholder="Search activity"
          required
        />
      </div>

      {isProjectClosed && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900 dark:border-yellow-700">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Project has ended. Activities cannot be modified.
          </p>
        </div>
      )}

      {/* Selected Activities Header */}
      <div className="w-full pl-2">
        <div className="flex justify-between items-center my-3">
          <h2 className="text-lg font-bold">Selected Activities</h2>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={isProjectClosed}
              onClick={() => setShowAddModal(true)}
              className={`text-white font-medium rounded-lg text-sm px-4 py-2 text-center transition-all ${
                isProjectClosed
                  ? "bg-gray-400 cursor-not-allowed opacity-60 dark:bg-gray-600"
                  : "bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
              }`}
              title={
                isProjectClosed ? "Project has ended." : "Add a new activity"
              }
            >
              Add Activity
            </button>
            <button
              type="button"
              disabled={isProjectClosed}
              onClick={submitActivity}
              className={`text-white font-medium rounded-lg text-sm px-4 py-2 text-center transition-all ${
                isProjectClosed
                  ? "bg-gray-400 cursor-not-allowed opacity-60 dark:bg-gray-600"
                  : "bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
              }`}
              title={
                isProjectClosed ? "Project has ended. Cannot save changes." : ""
              }
            >
              Save
            </button>
          </div>
        </div>
        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3">
                Activity
              </th>
              <th scope="col" className="w-12 px-4 py-3 text-center">
                Enabled
              </th>
              <th scope="col" className="w-16 px-4 py-3 text-center"></th>
            </tr>
          </thead>
          <tbody>
            {activityList &&
              activityList
                .filter((item) =>
                  item.activity
                    .toLowerCase()
                    .includes(searchActivitiesName.toLowerCase()),
                )
                .map((activity) => (
                  <tr
                    key={activity.version_id}
                    className={`border-b dark:border-gray-700 ${
                      isProjectClosed
                        ? "bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800"
                        : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600"
                    }`}
                  >
                    <td className="px-6 py-4 flex items-center gap-2">
                      {activity.activity}
                      {activity.is_mandatory && (
                        <span
                          title="Mandatory activity"
                          className="text-amber-500 text-base leading-none"
                        >
                          *
                        </span>
                      )}
                    </td>
                    <td className="w-12 px-4 py-4">
                      <div className="flex items-center justify-center">
                        <input
                          id={`checkbox-table-search-${activity.version_id}`}
                          type="checkbox"
                          disabled={isProjectClosed}
                          className={`w-4 h-4 rounded focus:ring-2 ${
                            isProjectClosed
                              ? "bg-gray-200 border-gray-300 cursor-not-allowed dark:bg-gray-700 dark:border-gray-600"
                              : "bg-gray-100 border-gray-300 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                          }`}
                          checked={activity.is_enabled}
                          onChange={(e) =>
                            handleSelect(activity.version_id, e.target.checked)
                          }
                          title={
                            isProjectClosed
                              ? "Project has ended. Cannot modify activities."
                              : ""
                          }
                        />
                      </div>
                    </td>
                    <td className="w-16 px-4 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(activity)}
                        disabled={isProjectClosed}
                        title={
                          activity.is_mandatory
                            ? "Mandatory activity"
                            : isProjectClosed
                              ? "Project has ended"
                              : "Remove activity from project"
                        }
                        className="text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed dark:text-red-400 dark:hover:text-red-300 dark:disabled:text-gray-600 transition-colors"
                      >
                        <svg
                          className="w-4 h-4 inline"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Add Activity Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAddModal(false);
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Add New Activity
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setNewActivityName("");
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                aria-label="Close modal"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div className="mb-5">
              <label
                htmlFor="new-activity-name"
                className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Activity Name
              </label>
              <input
                id="new-activity-name"
                type="text"
                value={newActivityName}
                onChange={(e) => setNewActivityName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddActivity();
                }}
                placeholder="Enter activity name"
                className="block w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setNewActivityName("");
                }}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 dark:focus:ring-gray-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddActivity}
                disabled={!newActivityName.trim() || isAdding}
                className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {isAdding ? "Saving..." : "Save Activity"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && activityToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteModal(false);
              setActivityToDelete(null);
            }
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Remove Activity
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to remove <strong>"{activityToDelete.activity}"</strong> from this project?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setActivityToDelete(null);
                }}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 dark:focus:ring-gray-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-300 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {isDeleting ? "Removing..." : "Yes, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mandatory Activity Warning Popup */}
      {mandatoryWarning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) setMandatoryWarning(null);
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6 text-center">
            <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900 mb-4">
              <svg
                className="w-6 h-6 text-amber-600 dark:text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Action Restricted
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              {mandatoryWarning}
            </p>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setMandatoryWarning(null)}
                className="px-6 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 focus:ring-4 focus:outline-none focus:ring-amber-300 transition-all"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectActivities;
